import { HttpErrorResponse } from "@angular/common/http";
import { Component, Input, OnDestroy, OnInit, signal, ViewChild } from "@angular/core";
import { ChartJsContainerComponent, rgb } from "@tylertech/chart-js";
import { ChartBindingObject, ChartDialogService, ChartManagerService, ChartRegistryService } from "@tylertech/charting-module";
import { Command, IconCommand } from "@tylertech/commands-module";
import { FileService, LoggingService } from "@tylertech/core-module";
import { ICustomViewComponent } from "@tylertech/custom-view";
import { IntentService } from "@tylertech/intents-module";
import { Utilities } from "@tylertech/shared-module";
import { ExecutionResult, IChartCriteria, ProfileSettingViewModel, } from "@tylertech/tyler-api-types";
import { ActiveElement, Chart, ChartConfiguration, ChartEvent, LegendElement, LegendItem, TooltipItem } from "chart.js";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";

@Component({
    selector: "utilities-connecting-disconnecting-accounts-chart",
    templateUrl: "./connectingDisconnectingAccountsChart.tmpl.html",
    standalone: false
})
export class UtilitiesConnectingDisconnectingAccountsChartComponent
    implements OnDestroy, OnInit, ICustomViewComponent<ChartBindingObject>
{
    private readonly destructor$ = new Subject<void>();
    readonly commands: ReadonlyArray<Command>;
    readonly customDataBinding: ChartBindingObject;
    private readonly _chartId: string;

    @ViewChild(ChartJsContainerComponent) private readonly chartContainer: ChartJsContainerComponent;
    readonly showChart = signal(false);
    chartConfig: ChartConfiguration<"line">;

    ChartSubtitle: string;
    private _chartCriteria: IChartCriteria;
    readonly ChartTitle: string = "Connecting vs. Disconnecting Accounts";
    isLoaded: boolean;
    constructor(
        $chartManagerService: ChartManagerService,
        private readonly $chartRegistryService: ChartRegistryService,
        private readonly $chartDialogService: ChartDialogService,
        private readonly $loggingService: LoggingService,
        private readonly $fileService: FileService,
        private readonly $intentService: IntentService
    )
    {
        $chartManagerService.refreshChartObservable.pipe(takeUntil(this.destructor$)).subscribe((chartId: string) =>
        {
            if (chartId !== this.chartId)
            {
                return;
            }
            this.loadChartData();
        });
        this.commands = [
            new IconCommand({
                icon: "share",
                label: null,
                action: () => this.shareChart(),
                getEnabled: () => this.chartHasData,
                tooltip: `Share ${this.ChartTitle}`,
            }),
        ];
    }

    get chartId(): string | undefined
    {
        return this._chartId || this.customDataBinding?.chartId;
    }

    @Input()
    get chartCriteria(): IChartCriteria | undefined
    {
        return this._chartCriteria || this.customDataBinding?.chartCriteria;
    }

    set chartCriteria(value: IChartCriteria)
    {
        this._chartCriteria = value;
    }

    get chartHasData(): boolean
    {
        return this.hasData();
    }

    get errorMessage(): string | undefined
    {
        return this.chartCriteria?.ErrorMessage;
    }

    ngOnDestroy(): void
    {
        this.destructor$.next();
        this.destructor$.complete();
    }

    ngOnInit(): void
    {
        new Promise(
            () =>
            {
                if (!this.hasData())
                {
                    return this.loadChartData();
                }

                this.updateChartConfiguration();
            })
            .finally(() =>
            {
                this.isLoaded = true;
            });
    }

    hasData(): boolean
    {
        return (
            this.chartCriteria?.ChartDatas?.length > 0 &&
            this.chartCriteria.ChartDatas[0].Data?.length > 0 &&
            this.chartCriteria.ChartDatas[0].Data.length > 0
        );
    }

    private loadChartData(): Promise<void>
    {
        this.isLoaded = false;
        return this.$chartRegistryService.GetChartData(this.chartCriteria)
            .then(
                (result: IChartCriteria) =>
                {
                    this.chartCriteria = result;
                    if (!this.hasData())
                    {
                        return;
                    }

                    this.updateChartConfiguration();
                },
                (error: HttpErrorResponse) =>
                {
                    this.$loggingService.error("Could not retrieve chart data", error);
                    this.chartCriteria = {
                        ErrorMessage: error.error.Message,
                    } as IChartCriteria;
                })
            .finally(() =>
            {
                this.isLoaded = true;
            });
    }

    private shareChart(): void
    {
        const img = this.chartContainer.createImage();
        this.$fileService.openDocumentLink(img, "image/png", this.ChartTitle.replace(/ /g, ""));
    }

    private updateChartConfiguration(): void
    {
        this.chartConfig = {
            type: "line",
            options: {
                responsive: true,
                plugins: {
                    legend: {
                        display: true,
                        position: "bottom",
                        onClick: (_event: ChartEvent, item: LegendItem, legend: LegendElement<"line">) =>
                        {
                            const meta = legend.chart.getDatasetMeta(item.datasetIndex);
                            meta.hidden = !meta.hidden;
                            legend.chart.update();
                        },
                        labels: { boxWidth: 12, color: "#333333" },
                    },
                    tooltip: {
                        callbacks: {
                            title: (tooltipItems: TooltipItem<"line">[]) =>
                            {
                                return tooltipItems[0].label;
                            },
                            label: (tooltipItem: TooltipItem<"line">): string | string[] =>
                            {
                                return tooltipItem.label;
                            },
                        },
                        backgroundColor: '#ffffff',
                        borderColor: '#767676',
                        borderWidth: 1
                    },
                },
                onClick: (event: ChartEvent & Event, _elements: ActiveElement[], chart: Chart): void =>
                {
                    const elements = chart.getElementsAtEventForMode(event, 'nearest', { intersect: true }, true);
                    const element = elements[0];
                    if (element)
                    {
                        this.openConnectingDisconnectingAccountsList(element.index);
                    }
                },
            },
            data: {
                labels: this.chartCriteria.ChartDatas[0].Labels,
                datasets: this.chartCriteria.ChartDatas
                    .map((data) =>
                    {
                        return {
                            type: "line",
                            lineTension: 0,
                            label: data.SetLabel,
                            data: data.Data,
                            fill: false,
                            borderColor: data.Additional[0],
                            backgroundColor: data.Additional[0],
                            pointBorderColor: rgb(255, 255, 255, 0),
                            pointBackgroundColor: rgb(255, 255, 255, 0),
                            pointHoverBackgroundColor: data.Additional[0],
                            pointHoverBorderColor: data.Additional[0],
                            pointHitRadius: 10,
                        };
                    }),
            },
        };

        this.showChart.set(this.chartCriteria?.ChartDatas?.length > 0);
    }

    displayChartParameterDialogAndReload(): void
    {
        this.$chartDialogService
            .show(this.chartCriteria.Parameters, this.ChartTitle)
            .then((result: ProfileSettingViewModel[]) =>
            {
                if (!Utilities.arraysEqual(this.chartCriteria.Parameters, result))
                {
                    this.chartCriteria.Parameters = result;
                    return this.loadChartData();
                }
            });
    }

    private openConnectingDisconnectingAccountsList(index): void
    {
        if (index === undefined || index === null) return;
        const from = new Date(
            this.chartCriteria?.Parameters?.find(v => v.Id === "ConnectingDisconnectingAccountsChartFromDate")?.Value
        );
        const retMonth = ((from.getMonth() + index) % 12) + 1;
        const retYear = from.getFullYear() + Math.floor((index + from.getMonth() - 1) / 12);
        new Promise((resolve, reject) =>
        {
            try
            {
                const inputJson = JSON.stringify({
                    month: retMonth,
                    year: retYear,
                });
                this.$intentService
                    .resolve("OpenConnectingDisconnectingAccountsSearch", "Open", { id: inputJson })
                    .then((responseJson: string) =>
                    {
                        const result = JSON.parse(responseJson) as ExecutionResult<string>;
                        if (result.Success) resolve(result);
                        else reject(result);
                    });
            } catch (err)
            {
                reject(<ExecutionResult<string>>{ Success: false });
            }
        });
    }
}
